/**
 * useWorkspaceFileActions — shared Digital Lab workspace file operations.
 *
 * Extracted from DigitalLabWorkspace.jsx so the project page and My Files use
 * ONE implementation of the two things that are easy to get subtly wrong:
 *
 *   1. The workspace_token expiry retry. The backend token lives 5 minutes.
 *      A student who leaves a project open while working in Premiere will
 *      come back to an expired token, so every Agent request that carries one
 *      must be able to refresh and retry exactly once — not zero times (the
 *      student sees a spurious error) and not repeatedly (a retry loop).
 *
 *   2. Native file opening. Mapped creative types launch their approved
 *      application through the Agent's secure launch flow; everything else
 *      opens with the Windows default association.
 *
 * Application-agnostic by construction: nothing here names a specific
 * application. Extensions map to whichever approved application on this
 * workstation matches by keyword, and any unmapped extension falls through to
 * the Windows default association. Supporting a new creative application is a
 * row in EXT_APP_KEYWORDS or simply the OS default — never a code path.
 */

import { useCallback, useMemo, useState } from 'react'
import api from '../services/api'
import toast from 'react-hot-toast'

export const AGENT_URL = import.meta.env.VITE_AGENT_URL || 'http://127.0.0.1:8750'

export async function fetchDeviceCode() {
  const res = await fetch(`${AGENT_URL}/device-code`)
  if (!res.ok) throw new Error('Agent not reachable')
  const data = await res.json()
  return data.device_code
}

export function getExt(name) {
  const m = /\.([a-z0-9]+)$/i.exec(name || '')
  return m ? m[1].toLowerCase() : ''
}

// Human-readable file type, shown under a filename. Two parts: the format and
// the application a student associates with it ("PSD · Photoshop"). Shared by
// My Files and the project workspace so a file reads identically in both.
export const FILE_TYPE_LABELS = {
  psd: 'PSD · Photoshop',
  psb: 'PSB · Photoshop',
  ai: 'AI · Illustrator',
  ait: 'AIT · Illustrator',
  eps: 'EPS · Illustrator',
  indd: 'INDD · InDesign',
  indt: 'INDT · InDesign',
  idml: 'IDML · InDesign',
  prproj: 'PRPROJ · Premiere Pro',
  prtl: 'PRTL · Premiere Pro',
  aep: 'AEP · After Effects',
  aet: 'AET · After Effects',
  lrcat: 'LRCAT · Lightroom',
  lrtemplate: 'LRTEMPLATE · Lightroom',
  sesx: 'SESX · Audition',
  fla: 'FLA · Animate',
  xfl: 'XFL · Animate',
  jpg: 'JPG · Image',
  jpeg: 'JPEG · Image',
  png: 'PNG · Image',
  gif: 'GIF · Image',
  bmp: 'BMP · Image',
  tiff: 'TIFF · Image',
  tif: 'TIF · Image',
  webp: 'WEBP · Image',
  svg: 'SVG · Image',
  mp4: 'MP4 · Video',
  mov: 'MOV · Video',
  avi: 'AVI · Video',
  mkv: 'MKV · Video',
  wmv: 'WMV · Video',
  m4v: 'M4V · Video',
  mp3: 'MP3 · Audio',
  wav: 'WAV · Audio',
  aac: 'AAC · Audio',
  pdf: 'PDF · Document',
  txt: 'TXT · Text',
  csv: 'CSV · Data',
  json: 'JSON · Data',
  zip: 'ZIP · Archive',
  rar: 'RAR · Archive',
  '7z': '7Z · Archive',
}

export function fileTypeLabel(name) {
  const ext = getExt(name)
  return FILE_TYPE_LABELS[ext] || (ext ? `${ext.toUpperCase()} · File` : 'File')
}

// Just the application half of the label ("Photoshop"), for places that already
// show other context and would otherwise read "Logo Design · PSD · Photoshop".
export function applicationLabel(name) {
  const label = fileTypeLabel(name)
  const parts = label.split(' · ')
  return parts.length > 1 ? parts[1] : parts[0]
}

// File extension → approved application keyword. The student's approved
// application list is matched by name (e.g. "Adobe Photoshop 2025" contains
// "photoshop"). The first approved app whose name contains the keyword wins.
export const EXT_APP_KEYWORDS = [
  { keyword: 'photoshop', exts: ['psd', 'psb'] },
  { keyword: 'illustrator', exts: ['ai', 'ait', 'eps'] },
  { keyword: 'indesign', exts: ['indd', 'indt', 'idml'] },
  { keyword: 'premiere', exts: ['prproj', 'prtl', 'psq'] },
  { keyword: 'after effects', exts: ['aep', 'aet'] },
  { keyword: 'lightroom', exts: ['lrcat', 'lrtemplate'] },
  { keyword: 'audition', exts: ['sesx', 'ses'] },
  { keyword: 'animate', exts: ['fla', 'xfl'] },
]

export function buildExtensionToAppMap(applications) {
  const map = {}
  for (const app of applications || []) {
    const name = (app.name || '').toLowerCase()
    for (const { keyword, exts } of EXT_APP_KEYWORDS) {
      if (name.includes(keyword)) {
        for (const ext of exts) {
          if (!map[ext]) map[ext] = app.id
        }
      }
    }
  }
  return map
}

/**
 * @param {object}   params
 * @param {string}   params.workspaceToken  current token (may be stale)
 * @param {function} params.refetchToken    react-query refetch for the token
 * @param {string}   params.deviceCode      this workstation's device code
 * @param {object}   params.extAppMap       extension → approved application id
 */
export function useWorkspaceFileActions({
  workspaceToken,
  refetchToken,
  deviceCode,
  extAppMap = {},
}) {
  const [opening, setOpening] = useState(null)
  const [revealing, setRevealing] = useState(false)

  // Centralised workspace-operation wrapper. Every Agent request that depends
  // on a workspace_token goes through this. On a 403 "Invalid or expired
  // workspace token" it forces a fresh token and retries the original
  // operation exactly once — no infinite loop, no second click.
  const withWorkspaceTokenRetry = useCallback(
    async (requestFn) => {
      let token = workspaceToken
      let res = await requestFn(token)
      if (res.status === 403) {
        let body = {}
        try {
          body = await res.clone().json()
        } catch {
          /* non-JSON 403 — not a token error, fall through */
        }
        if (body.error === 'Invalid or expired workspace token') {
          const fresh = refetchToken ? await refetchToken() : null
          token = fresh?.data?.workspace_token
          if (token) {
            res = await requestFn(token) // exactly one retry
          }
        }
      }
      return res
    },
    [workspaceToken, refetchToken]
  )

  /**
   * Open a file in its native application.
   * @param {object} entry      file entry — only .name is required
   * @param {string} folderPath workspace-relative folder containing the file
   */
  const openFile = useCallback(
    async (entry, folderPath) => {
      if (opening) return
      const filePath = folderPath ? `${folderPath}/${entry.name}` : entry.name
      setOpening(entry.name)
      try {
        const ext = getExt(entry.name)
        const appId = extAppMap[ext]
        // Build the request body lazily so the workspace_token is fresh after
        // a token refresh + retry.
        const buildBody = (token) => {
          const b = { workspace_token: token, file_path: filePath }
          if (appId) b.application_id = appId
          return b
        }

        if (appId) {
          const { data } = await api.post('/digital-lab/student/launch-request', {
            application_id: appId,
            device_code: deviceCode,
          })
          // launch_token is short-lived (30s) and not workspace-scoped, so it
          // doesn't participate in the workspace-token retry.
          const launchToken = data.launch_token
          const res = await withWorkspaceTokenRetry((token) =>
            fetch(`${AGENT_URL}/open-file`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ ...buildBody(token), launch_token: launchToken }),
            })
          )
          const result = await res.json()
          if (!res.ok) throw new Error(result.error || 'Could not open file')
          toast.success(`Opening in ${result.application?.name || 'application'}`)
        } else {
          const res = await withWorkspaceTokenRetry((token) =>
            fetch(`${AGENT_URL}/open-file`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(buildBody(token)),
            })
          )
          const result = await res.json()
          if (!res.ok) throw new Error(result.error || 'Could not open file')
          toast.success(`Opening ${entry.name}`)
        }
      } catch (err) {
        toast.error(err.response?.data?.error || err.message || 'Could not open file')
      } finally {
        setOpening(null)
      }
    },
    [withWorkspaceTokenRetry, deviceCode, extAppMap, opening]
  )

  /**
   * Open a workspace folder in Windows Explorer.
   *
   * Only reachable when the Agent reports explorer_reveal_enabled. The caller
   * is responsible for not rendering the control otherwise; this is the
   * second line of defence, not the first.
   */
  const revealFolder = useCallback(
    async (folderPath) => {
      if (revealing) return
      setRevealing(true)
      try {
        const res = await withWorkspaceTokenRetry((token) =>
          fetch(`${AGENT_URL}/workspace/reveal`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ workspace_token: token, path: folderPath }),
          })
        )
        const result = await res.json()
        if (!res.ok) throw new Error(result.error || 'Could not open Windows Explorer')
      } catch (err) {
        toast.error(err.message || 'Could not open Windows Explorer')
      } finally {
        setRevealing(false)
      }
    },
    [withWorkspaceTokenRetry, revealing]
  )

  /**
   * Download a workspace file through the Agent.
   */
  const downloadFile = useCallback(
    async (filePath) => {
      try {
        const res = await withWorkspaceTokenRetry((token) =>
          fetch(
            `${AGENT_URL}/workspace/download?token=${token}&path=${encodeURIComponent(filePath)}`
          )
        )
        if (!res.ok) {
          const data = await res.json()
          throw new Error(data.error || 'Download failed')
        }
        const blob = await res.blob()
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = filePath.split('/').pop().split('\\').pop()
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        URL.revokeObjectURL(url)
      } catch (err) {
        toast.error(err.message)
      }
    },
    [withWorkspaceTokenRetry]
  )

  return {
    withWorkspaceTokenRetry,
    openFile,
    revealFolder,
    downloadFile,
    opening,
    revealing,
  }
}

/**
 * Approved applications for this workstation, shaped into an extension map.
 * Kept here so both pages derive it identically.
 */
export function useExtensionAppMap(applicationsData) {
  return useMemo(
    () => buildExtensionToAppMap(applicationsData?.applications),
    [applicationsData?.applications]
  )
}
